import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Wallet } from "lucide-react";

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({ email: "", password: "", name: "" });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success("Accesso effettuato!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Credenziali non valide");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await register(registerData.email, registerData.password, registerData.name);
      toast.success("Registrazione completata!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la registrazione");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#007AFF] rounded-2xl mb-4">
            <Wallet className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-semibold text-[#1D1D1F] tracking-tight">
            Tesoreria Dashboard
          </h1>
          <p className="text-[#86868B] mt-2">
            Gestione finanziaria intelligente
          </p>
        </div>

        <Card className="bg-white border-0 shadow-[0_2px_20px_rgba(0,0,0,0.08)] rounded-2xl">
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-[#F5F5F7] rounded-xl m-4 p-1" style={{ width: 'calc(100% - 32px)' }}>
              <TabsTrigger 
                value="login" 
                className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="login-tab"
              >
                Accedi
              </TabsTrigger>
              <TabsTrigger 
                value="register"
                className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="register-tab"
              >
                Registrati
              </TabsTrigger>
            </TabsList>

            <CardContent className="pt-2">
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-[#1D1D1F]">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="nome@azienda.it"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      className="h-12 rounded-xl border-[#D2D2D7] focus:border-[#007AFF] focus:ring-[#007AFF]/10"
                      data-testid="login-email"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-[#1D1D1F]">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      className="h-12 rounded-xl border-[#D2D2D7] focus:border-[#007AFF] focus:ring-[#007AFF]/10"
                      data-testid="login-password"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full h-12 bg-[#007AFF] hover:bg-[#0071E3] text-white rounded-full font-medium transition-all active:scale-[0.98]"
                    data-testid="login-submit"
                  >
                    {isLoading ? <div className="spinner" /> : "Accedi"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name" className="text-[#1D1D1F]">Nome</Label>
                    <Input
                      id="register-name"
                      type="text"
                      placeholder="Mario Rossi"
                      value={registerData.name}
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      className="h-12 rounded-xl border-[#D2D2D7] focus:border-[#007AFF] focus:ring-[#007AFF]/10"
                      data-testid="register-name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email" className="text-[#1D1D1F]">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="nome@azienda.it"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      className="h-12 rounded-xl border-[#D2D2D7] focus:border-[#007AFF] focus:ring-[#007AFF]/10"
                      data-testid="register-email"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password" className="text-[#1D1D1F]">Password</Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="••••••••"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      className="h-12 rounded-xl border-[#D2D2D7] focus:border-[#007AFF] focus:ring-[#007AFF]/10"
                      data-testid="register-password"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full h-12 bg-[#007AFF] hover:bg-[#0071E3] text-white rounded-full font-medium transition-all active:scale-[0.98]"
                    data-testid="register-submit"
                  >
                    {isLoading ? <div className="spinner" /> : "Registrati"}
                  </Button>
                </form>
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>

        <p className="text-center text-[#86868B] text-sm mt-6">
          Dashboard Tesoreria v1.0
        </p>
      </div>
    </div>
  );
};

export default Login;
